import {
    Search,
    X,
    Users,
    Gamepad2,
    MapPin,
    CreditCard,
    Settings,
    ArrowRight,
} from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

interface SearchableItem {
    id: string;
    label: string;
    path: string;
    icon: React.ComponentType<{ size?: number; className?: string }>;
    category: string;
}

const searchableItems: SearchableItem[] = [
    { id: 'dashboard', label: 'Dashboard', path: '/admin', icon: Settings, category: 'Pages' },
    { id: 'users', label: 'All Users', path: '/admin/users', icon: Users, category: 'Pages' },
    { id: 'organizers', label: 'Organizer Management', path: '/admin/organizer-management', icon: Users, category: 'Organizer Management' },
    { id: 'games', label: 'Organizer Games', path: '/admin/games', icon: Gamepad2, category: 'Organizer Management' },
    { id: 'free-games', label: 'Organizer Free Games', path: '/admin/free-games', icon: Gamepad2, category: 'Organizer Management' },
    { id: 'game-approvals', label: 'Game Approvals', path: '/admin/game-approvals', icon: Gamepad2, category: 'Organizer Management' },
    { id: 'pitches', label: 'Organizer Pitches', path: '/admin/pitches', icon: MapPin, category: 'Organizer Management' },
    { id: 'pitch-approvals', label: 'Pitch Approvals', path: '/admin/pitch-approvals', icon: MapPin, category: 'Organizer Management' },
    { id: 'bookings', label: 'Organizer Bookings', path: '/admin/bookings', icon: MapPin, category: 'Organizer Management' },
    { id: 'game-payouts', label: 'Game Payouts', path: '/admin/game-payouts', icon: CreditCard, category: 'Organizer Management' },
    { id: 'leagues', label: 'Leagues', path: '/admin/leagues', icon: Gamepad2, category: 'Pages' },
    { id: 'events', label: 'Events', path: '/admin/events', icon: Gamepad2, category: 'Pages' },
    { id: 'transactions', label: 'Transactions', path: '/admin/transactions', icon: CreditCard, category: 'Pages' },
    { id: 'plans', label: 'Plans', path: '/admin/plans', icon: CreditCard, category: 'Pages' },
    { id: 'blogs', label: 'Blog', path: '/admin/blogs', icon: Settings, category: 'Pages' },
    { id: 'settings', label: 'Settings', path: '/admin/settings', icon: Settings, category: 'Pages' },
    { id: 'locations', label: 'Locations', path: '/admin/locations', icon: MapPin, category: 'Pages' },
    { id: 'notifications', label: 'Notifications', path: '/admin/notifications', icon: Settings, category: 'Pages' },
    { id: 'projects', label: 'Projects', path: '/admin/projects', icon: Settings, category: 'Pages' },
];

interface GlobalSearchProps {
    isOpen: boolean;
    onClose: () => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ isOpen, onClose }) => {
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    // Filter items based on query
    const filteredItems = searchableItems.filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase())
    );

    // Group by category
    const groupedItems = filteredItems.reduce<Record<string, SearchableItem[]>>((acc, item) => {
        const bucket = acc[item.category] ?? [];
        bucket.push(item);
        acc[item.category] = bucket;
        return acc;
    }, {});

    // Focus input when opened
    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
            setQuery('');
            setSelectedIndex(0);
        }
    }, [isOpen]);

    // Keyboard navigation
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex((prev) => Math.min(prev + 1, filteredItems.length - 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex((prev) => Math.max(prev - 1, 0));
            } else if (e.key === 'Enter') {
                const selectedItem = filteredItems[selectedIndex];
                if (selectedItem) {
                    navigate(selectedItem.path);
                    onClose();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, selectedIndex, filteredItems, navigate, onClose]);

    if (!isOpen) return null;
    if (typeof document === 'undefined') return null;

    return createPortal(
        <div className="fixed inset-0 z-[100] overflow-y-auto">
            {/* Overlay */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative min-h-screen flex items-start justify-center pt-[15vh] px-4">
                <div className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden">
                    {/* Search input */}
                    <div className="flex items-center gap-3 px-4 border-b border-slate-200">
                        <Search size={20} className="text-slate-400" />
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Search pages, users, actions..."
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setSelectedIndex(0);
                            }}
                            className="flex-1 py-4 text-base outline-none placeholder:text-slate-400"
                        />
                        <button
                            onClick={onClose}
                            className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Results */}
                    <div className="max-h-[400px] overflow-y-auto py-2">
                        {filteredItems.length === 0 ? (
                            <div className="px-4 py-8 text-center text-slate-500">
                                No results found for "{query}"
                            </div>
                        ) : (
                            Object.entries(groupedItems).map(([category, items]) => (
                                <div key={category}>
                                    <div className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                                        {category}
                                    </div>
                                    {items.map((item) => {
                                        const globalIndex = filteredItems.indexOf(item);
                                        const Icon = item.icon;
                                        const isSelected = globalIndex === selectedIndex;

                                        return (
                                            <button
                                                key={item.id}
                                                onClick={() => {
                                                    navigate(item.path);
                                                    onClose();
                                                }}
                                                onMouseEnter={() => setSelectedIndex(globalIndex)}
                                                className={`
                          flex items-center gap-3 w-full px-4 py-2.5 text-left
                          transition-colors
                          ${isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-slate-50 text-slate-700'}
                        `}
                                            >
                                                <Icon size={18} className={isSelected ? 'text-primary' : 'text-slate-400'} />
                                                <span className="flex-1 font-medium">{item.label}</span>
                                                <ArrowRight size={14} className={`${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                                            </button>
                                        );
                                    })}
                                </div>
                            ))
                        )}
                    </div>

                    {/* Footer hint */}
                    <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-500 flex items-center gap-4">
                        <span className="flex items-center gap-1">
                            <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 text-[10px]">↑</kbd>
                            <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 text-[10px]">↓</kbd>
                            to navigate
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 text-[10px]">↵</kbd>
                            to select
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 text-[10px]">esc</kbd>
                            to close
                        </span>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default GlobalSearch;
